import { formatE164PhoneNumber, parsePhone } from '@alluradirect/react';
import { TextField } from '@mui/material';
import styled from 'styled-components';
import {
  CountryCallingCodePicker,
  useCountryCallingCodePicker,
} from 'components/formik/core/FormikPhoneNumber/CountryCallingCodePicker';
import { useField } from 'formik';
import React, { useEffect, useRef } from 'react';
import { AlluraFormikErrorMessage } from '../AlluraErrorMessage/AlluraFormikErrorMessage';

import Grid from 'components/core/Grid';

interface Props {
  // Formik field name
  name: string;
  disabled?: boolean;
  dataTestId?: string | undefined;
  autoFocus?: boolean;

  onBlur?: () => void;
  onFocus?: () => void;
  onChange?: () => void;
}

const CountryCodeWrapper = styled.div`
  & fieldset {
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
    border-bottom: 0px;
  }
`;

const StyledTextField = styled(TextField)`
  & fieldset {
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
  }

  & .Mui-disabled {
    pointer-events: none;
  }
`;

const FormikPhoneNumber = ({
  name,
  disabled = false,
  dataTestId = undefined,
  autoFocus = false,
  onFocus = undefined,
  onBlur = undefined,
  onChange = undefined,
}: Props) => {
  const textFieldRef = useRef<any>(null);
  const [field, , helpers] = useField(name);

  const [subscriberNumber, setSubscriberNumber] = React.useState(parsePhone(field.value).subscriberNumber);

  function onChangeText(e: any) {
    onChange && onChange();
    field.onChange(e);
    setSubscriberNumber(e.target.value);
  }

  const countryPickerInfo = useCountryCallingCodePicker(field.value);

  useEffect(() => {
    if (subscriberNumber.trim().length === 0) return;

    const e164Number = formatE164PhoneNumber({
      subscriberNumber: subscriberNumber.trim(),
      countryCallingCode: countryPickerInfo.callingCode,
    });
    helpers.setValue(e164Number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriberNumber, countryPickerInfo.callingCode]);

  const handleFocus = () => {
    onFocus && onFocus();
  };

  const handleBlur = (e: any) => {
    onBlur && onBlur();
    field.onBlur(e);
    helpers.setTouched(true);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <CountryCodeWrapper>
          <CountryCallingCodePicker {...countryPickerInfo} disabled={disabled} />
        </CountryCodeWrapper>
        <StyledTextField
          inputRef={textFieldRef}
          disabled={disabled}
          label={''}
          value={subscriberNumber}
          onChange={onChangeText}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={disabled ? 'Please verify a phone number' : 'Enter phone number'}
          fullWidth
          size="small"
          data-testid={dataTestId}
          autoFocus={autoFocus}
        />
        <AlluraFormikErrorMessage fieldName={name} />
      </Grid>
    </Grid>
  );
};

export default FormikPhoneNumber;
